import { useEffect, useMemo, useState } from "react";
import { Download, ExternalLink, FileText, Printer } from "lucide-react";
import { toast } from "sonner";

import SocietyLetterhead from "../../components/SocietyLetterhead";
import { Button } from "../../components/ui/button";
import { Loading, PageHeader } from "../../components/shared";
import api, { API } from "../../lib/api";

function readiness(profile) {
  return [
    ["Society Name", Boolean(profile?.name), false],
    ["Address", Boolean(profile?.address), false],
    ["Registration Number", Boolean(profile?.registration_number), false],
    ["Logo", Boolean(profile?.has_logo), true],
    ["Regional Name", Boolean(profile?.regional_name), true],
    [
      "Contact Details",
      Boolean(profile?.primary_contact || profile?.official_email),
      false,
    ],
  ];
}

export default function SocietyLetterheadPage() {
  const [profile, setProfile] = useState(null);
  const [signatory, setSignatory] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = async () => {
    try {
      const profileResponse = await api.get("/society-setup/profile");
      setProfile(profileResponse.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
        setProfile(null);
      } else {
        toast.error(error.response?.data?.detail || "Could not load letterhead details");
        setProfile({});
      }
    }
    try {
      const governanceResponse = await api.get("/society-setup/governance");
      setSignatory(governanceResponse.data.signatories?.[0] || null);
    } catch {
      setSignatory(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const status = useMemo(() => readiness(profile), [profile]);
  const complete = status
    .filter(([, , optional]) => !optional)
    .every(([, ready]) => ready);

  const downloadSample = async () => {
    try {
      const response = await fetch(`${API}/pdf/society-letterhead/sample`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Could not create the sample PDF");
      const url = URL.createObjectURL(await response.blob());
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(error.message || "Could not create the sample PDF");
    }
  };

  if (accessDenied) {
    return (
      <div data-testid="society-letterhead-access-denied">
        <PageHeader title="Society Letterhead" />
        <section className="border-l-4 border-amber-500 bg-amber-50 p-6">
          <h2 className="font-display text-xl font-bold text-slate-950">Access not assigned</h2>
          <p className="mt-2 text-sm text-slate-700">
            Your Society role does not currently include access to Letterhead documents.
          </p>
        </section>
      </div>
    );
  }

  if (!profile) return <Loading />;

  return (
    <div data-testid="society-letterhead-page">
      <style>{`
        @media print {
          .letterhead-page-controls, .letterhead-page-heading { display: none !important; }
          .letterhead-print-area { box-shadow: none !important; }
        }
      `}</style>
      <div className="letterhead-page-heading">
        <PageHeader
          subtitle="Your Society profile is used consistently for printable official documents."
          title="Society Letterhead"
        >
          <Button
            data-testid="society-letterhead-print-button"
            onClick={() => window.print()}
            variant="outline"
          >
            <Printer className="h-4 w-4" />
            Print preview
          </Button>
          <Button
            data-testid="society-letterhead-download-button"
            onClick={downloadSample}
          >
            <Download className="h-4 w-4" />
            Download sample PDF
          </Button>
        </PageHeader>
      </div>

      <section
        className="letterhead-page-controls mb-7 border-l-4 border-emerald-600 bg-emerald-50 p-5"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
              Letterhead Profile
            </p>
            <p className="mt-1 font-display text-xl font-bold text-slate-950">
              {complete ? "Ready" : "Profile Incomplete"}
            </p>
          </div>
          {!complete && (
            <a
              data-testid="society-letterhead-complete-profile-link"
              href="/app/setup"
            >
              <Button variant="outline">
                <ExternalLink className="h-4 w-4" />
                Complete Society Profile
              </Button>
            </a>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          {status.map(([label, ready, optional]) => (
            <span
              data-testid={`letterhead-readiness-${label.toLowerCase().replaceAll(" ", "-")}`}
              key={label}
            >
              <span
                className={
                  ready
                    ? "text-emerald-700"
                    : optional
                      ? "text-slate-500"
                      : "text-amber-700"
                }
              >
                {ready ? "✓" : optional ? "○" : "!"}
              </span>{" "}
              {label}{optional ? " — Optional" : ""}
            </span>
          ))}
        </div>
      </section>

      <div
        className={
          "letterhead-print-area mx-auto max-w-4xl border border-slate-200 bg-slate-100 " +
          "p-3 sm:p-6"
        }
      >
        <SocietyLetterhead profile={profile} signatory={signatory}>
          <div
            className="space-y-4"
            data-testid="society-letterhead-sample-body"
          >
            <p className="font-semibold text-slate-900">SAMPLE LETTERHEAD</p>
            <p>
              This is a preview of the official Society document format. Generated documents remain
              drafts until reviewed and confirmed by an authorized Society user.
            </p>
            <p>
              The profile details above are reused for eligible notices, letters, meeting documents,
              and other printable Society records.
            </p>
          </div>
        </SocietyLetterhead>
      </div>
      <p className="letterhead-page-controls mt-4 flex items-center gap-2 text-xs text-slate-500">
        <FileText className="h-4 w-4" />
        Society details are controlled in Society Setup and are never copied from another Society.
      </p>
    </div>
  );
}